import React, { useState, useEffect, useMemo, useCallback } from 'react';
import expensePresets from '../config/expensePresets';
import { calculateKilometricAmount } from '../utils/expenseCalculations';
import { handleFileChange, handlePreview, handlePreviewExisting, handleRemoveExistingFile } from '../utils/fileHandlers';
import { validateExpenseForm, getMainDate } from '../utils/expenseValidations';
import { toInputDateFormat } from '../utils/dateUtils';
import { calculateRoadDistance, formatDuration } from '../utils/distanceCalculator';
import { useLocation } from 'react-router-dom';
import AddressAutocomplete from './AddressAutocomplete';
import Swal from 'sweetalert2';

const parseAmountValue = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const calculateTtcFromHt = (amountHt, tva) => {
  const htValue = parseAmountValue(amountHt);
  const tvaPercentage = parseAmountValue(tva);
  return htValue * (1 + tvaPercentage / 100);
};

const calculateHtFromTtc = (amountTtc, tva) => {
  const ttcValue = parseAmountValue(amountTtc);
  const tvaPercentage = parseAmountValue(tva);

  if (tvaPercentage <= 0) {
    return ttcValue;
  }

  return ttcValue / (1 + tvaPercentage / 100);
};

const ExpenseForm = ({
  expenseType,
  noteId,
  onSave,
  onCancel,
  existingExpense,
  employeeId,
  formId,
  showSubmitButton = true
}) => {
  const preset = expensePresets[expenseType] || expensePresets.other;
  const location = useLocation();
  const defaultPresetDescription = preset.defaultValues?.description || '';

  const shouldUseOCRDescription = useCallback((currentDescription, ocrDescription) => {
    if (!ocrDescription) return false;

    const trimmedCurrent = (currentDescription || '').trim();
    const trimmedOCR = ocrDescription.trim();
    const trimmedPreset = defaultPresetDescription.trim();

    if (!trimmedCurrent) return true;
    if (trimmedCurrent === trimmedOCR) return true;
    if (trimmedPreset && trimmedCurrent === trimmedPreset) return true;

    return false;
  }, [defaultPresetDescription]);

  const applyOCRDescriptionToSpecificField = useCallback((values, ocrDescription) => {
    if (!ocrDescription) return values;

    const specificFieldMap = {
      restaurant: 'nom_restaurant',
      hotel: 'nom_hotel',
      carRental: 'societe_location',
      carburant: 'description',
      other: 'description'
    };

    const targetField = specificFieldMap[expenseType];
    if (!targetField || targetField === 'description') {
      return values;
    }

    const currentValue = (values[targetField] || '').trim();
    const fieldConfig = preset.fields.find(field => field.id === targetField);
    const placeholder = (fieldConfig?.placeholder || '').trim();

    if (!currentValue || currentValue === placeholder) {
      return {
        ...values,
        [targetField]: ocrDescription
      };
    }

    return values;
  }, [expenseType, preset.fields]);

  const normalizeOCRDateForInput = useCallback((ocrDate, logLabel = 'OCR') => {
    let formattedDate = ocrDate;

    if (!ocrDate) {
      return formattedDate;
    }

    try {
      if (typeof ocrDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(ocrDate)) {
        return ocrDate;
      }

      if (typeof ocrDate === 'string' && /^\d{2}[/-]\d{2}[/-]\d{4}$/.test(ocrDate)) {
        const parts = ocrDate.split(/[/-]/);
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }

      const date = new Date(ocrDate);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }

      console.error(`Date invalide après new Date() dans ${logLabel}:`, ocrDate);
      return new Date().toISOString().split('T')[0];
    } catch (error) {
      console.error(`Erreur de conversion de date dans ${logLabel}:`, error, 'Date:', ocrDate);
      return new Date().toISOString().split('T')[0];
    }
  }, []);

  const buildValuesFromOCR = useCallback((currentValues, ocrData) => {
    const formattedDate = normalizeOCRDateForInput(ocrData.date, 'buildValuesFromOCR');
    const tvaRate = ocrData.tva || currentValues.tva || (preset.defaultValues?.tva || 20);
    const montantHT = ocrData.montantHT ?? ocrData.amount ?? '';
    const montantTTC = ocrData.montantTTC ?? (montantHT !== '' ? calculateTtcFromHt(montantHT, tvaRate) : '');

    const nextValues = {
      ...currentValues,
      amount: montantTTC || currentValues.amount,
      date: formattedDate || currentValues.date,
      tva: tvaRate
    };

    if (montantHT !== '' && montantTTC !== '') {
      nextValues.montantHT = montantHT;
      nextValues.montantTTC = montantTTC;
      nextValues.tvaIncluded = ocrData.tvaIncluded;

      if (Math.abs(parseAmountValue(nextValues.amount) - parseAmountValue(montantTTC)) > 0.01) {
        console.warn(`Incohérence détectée - amount: ${nextValues.amount}€, montantTTC: ${montantTTC}€`);
        nextValues.amount = montantTTC;
      }
    }

    if (expenseType === 'hotel' && formattedDate) {
      nextValues.date_depart_hotel = formattedDate;
    }

    if (expenseType === 'carRental' && formattedDate) {
      nextValues.date_fin_loc = formattedDate;
    }

    if (expenseType === 'carburant') {
      if (ocrData.fuel_liters) {
        nextValues.litres = ocrData.fuel_liters;
      }

      if (ocrData.price_per_liter) {
        nextValues.prix_par_litre = ocrData.price_per_liter;
      }
    }

    if (shouldUseOCRDescription(currentValues.description, ocrData.description)) {
      nextValues.description = ocrData.description;
    }

    if (ocrData.descriptionNote) {
      nextValues.descriptionNote = ocrData.descriptionNote;
    }

    return applyOCRDescriptionToSpecificField(nextValues, ocrData.description);
  }, [
    applyOCRDescriptionToSpecificField,
    expenseType,
    normalizeOCRDateForInput,
    preset.defaultValues,
    shouldUseOCRDescription
  ]);
  
  const initialValues = useMemo(() => {
    const values = {
      amount: '',
      description: '',
      date: new Date().toISOString().split('T')[0], // Date du jour par défaut
      tva: preset.noTva ? 0 : (preset.defaultValues?.tva || 20) // TVA selon le preset ou 20 par défaut
    };
    

    
    if (typeof preset.defaultValues === 'function') {
      preset.defaultValues(employeeId).then(defaults => {
        Object.keys(defaults).forEach(key => {
          values[key] = defaults[key];
        });
      });
    } else {
      preset.fields.forEach(field => {
        values[field.id] = preset.defaultValues?.[field.id] || '';
      });
      
      // Appliquer les autres valeurs par défaut du preset (comme description, tva, etc.)
      if (preset.defaultValues) {
        Object.keys(preset.defaultValues).forEach(key => {
          if (values[key] === undefined || values[key] === '') {

            values[key] = preset.defaultValues[key];
          }
        });

      }
    }

    if (existingExpense) {
      let specificData = {};
      try {
        specificData = typeof existingExpense.donnees_specifiques === 'string' 
          ? JSON.parse(existingExpense.donnees_specifiques) 
          : existingExpense.donnees_specifiques || {};
      } catch (e) {
        specificData = {};
      }
      
      Object.keys(specificData).forEach(key => {
        values[key] = specificData[key];
      });
      
      values.amount = existingExpense.montant || existingExpense.amount || '';
      values.description = existingExpense.description || '';
      
      // Formater la date pour les inputs de type date
      if (existingExpense.date_depense) {
        const date = new Date(existingExpense.date_depense);
        values.date = date.toISOString().split('T')[0];
      } else if (existingExpense.date) {
        const date = new Date(existingExpense.date);
        values.date = date.toISOString().split('T')[0];
      }
    }
    
    return values;
  }, [preset, existingExpense, employeeId]);

  const [formValues, setFormValues] = useState(initialValues);
  const [existingFiles, setExistingFiles] = useState([]);
  const [fileInputs, setFileInputs] = useState([]);
  const [tauxKilometrique, setTauxKilometrique] = useState(0.603);
  const [kilometric_info, setKilometricInfo] = useState(null);
  
  // États pour la gestion des adresses et distances
  const [addressCoordinates, setAddressCoordinates] = useState({
    lieu_depart_dkm: null,
    destination_dkm: null
  });
  const [distanceInfo, setDistanceInfo] = useState({
    isCalculating: false,
    error: null,
    duration: null,
    method: null // 'road' ou 'crow_flies'
  });
  const [calculating, setCalculating] = useState(false);

  // Fonction pour gérer la sélection d'une adresse
  const handleAddressSelect = async (fieldId, addressData) => {
    console.log('Adresse sélectionnée pour', fieldId, ':', addressData);
    
    // Sauvegarder les coordonnées
    setAddressCoordinates(prev => ({
      ...prev,
      [fieldId]: addressData.coordinates
    }));
    
    // Mettre à jour les coordonnées dans les données du formulaire pour la sauvegarde
    setFormValues(prev => ({
      ...prev,
      [`${fieldId}_coordinates`]: addressData.coordinates,
      [`${fieldId}_city`]: addressData.city,
      [`${fieldId}_postcode`]: addressData.postcode
    }));
  };

  // Fonction pour calculer la distance entre les deux adresses
  const calculateDistance = async (fromCoordinates, toCoordinates) => {
    if (!fromCoordinates || !toCoordinates) {
      return;
    }

    setDistanceInfo(prev => ({ ...prev, isCalculating: true, error: null }));

    try {
      const result = await calculateRoadDistance(fromCoordinates, toCoordinates);
      
      // Mettre à jour la distance dans le formulaire
      setFormValues(prev => ({
        ...prev,
        distance_parcourue: result.distance
      }));

      setDistanceInfo({
        isCalculating: false,
        error: null,
        duration: result.duration,
        method: result.success ? 'road' : 'crow_flies'
      });

      // Afficher une info à l'utilisateur
      if (result.fallback) {
        console.warn('Calcul de distance en mode dégradé (estimation)');
      }

    } catch (error) {
      console.error('Erreur calcul distance:', error);
      setDistanceInfo({
        isCalculating: false,
        error: 'Impossible de calculer la distance',
        duration: null,
        method: null
      });
    }
  };

  // Effet pour calculer la distance quand les deux adresses sont sélectionnées
  useEffect(() => {
    if (expenseType === 'distance_km' && 
        addressCoordinates.lieu_depart_dkm && 
        addressCoordinates.destination_dkm) {
      calculateDistance(
        addressCoordinates.lieu_depart_dkm,
        addressCoordinates.destination_dkm
      );
    }
  }, [addressCoordinates.lieu_depart_dkm, addressCoordinates.destination_dkm, expenseType]);

  useEffect(() => {
    if (expenseType === 'distance_km') {
      fetch('/api/employee/kilometric-rate', { credentials: 'include' })
        .then(res => res.json())
        .then(data => {
          if (data.taux_kilometrique) setTauxKilometrique(data.taux_kilometrique);
        });
    }
  }, [expenseType]);

  useEffect(() => {
    if (expenseType === 'distance_km' && tauxKilometrique) {
      setFormValues(prev => ({
        ...prev,
        taux_kilometrique: tauxKilometrique
      }));
    }
  }, [tauxKilometrique, expenseType]);

  useEffect(() => {
    if (expenseType === 'distance_km' && formValues.distance_parcourue) {
      const calculerMontant = async () => {
        setCalculating(true);
        try {
          // Passer l'ID de la dépense existante pour exclure sa distance du calcul annuel
          const expenseIdToExclude = existingExpense?.id_depense || null;
          const result = await calculateKilometricAmount(formValues.distance_parcourue, expenseIdToExclude);
          
          setFormValues(prev => ({
            ...prev,
            amount: result.montant ? result.montant.toFixed(2) : ''
          }));
          
          // Toujours définir kilometric_info même en cas d'erreur pour afficher les infos disponibles
          setKilometricInfo(result);
        } catch (error) {
          console.error('Erreur calcul kilométrique:', error);
          // Fallback sur ancien calcul avec informations d'erreur
          const montantFallback = parseFloat(formValues.distance_parcourue) * (formValues.taux_kilometrique || 0.631);
          setFormValues(prev => ({
            ...prev,
            amount: montantFallback ? montantFallback.toFixed(2) : ''
          }));
          
          // Créer un objet d'information avec l'erreur
          setKilometricInfo({
            montant: montantFallback,
            distance_annuelle_actuelle: 0,
            nouvelle_distance_annuelle: parseFloat(formValues.distance_parcourue),
            detail: { tranche_actuelle: 1 },
            cv: 0,
            error: error.message || 'Erreur lors du calcul kilométrique'
          });
        } finally {
          setCalculating(false);
        }
      };
      
      calculerMontant();
    } else if (expenseType !== 'distance_km') {
      setKilometricInfo(null);
    }
  }, [formValues.distance_parcourue, formValues.taux_kilometrique, expenseType, existingExpense?.id_depense]);

  useEffect(() => {
    if (existingExpense && existingExpense.justificatif) {
      try {
        let files = typeof existingExpense.justificatif === 'string'
          ? JSON.parse(existingExpense.justificatif)
          : existingExpense.justificatif;
        
        // Transformer les chemins de fichiers en objets avec name et path
        if (Array.isArray(files)) {
          files = files.map(filePath => {
            // Si c'est déjà un objet avec name et path, le garder tel quel
            if (typeof filePath === 'object' && filePath.name && filePath.path) {
              return filePath;
            }
            // Sinon, créer un objet à partir du chemin
            return {
              name: filePath.split('/').pop(), // Extraire le nom du fichier du chemin
              path: filePath
            };
          });
        }
        
        setExistingFiles(files);
      } catch {
        setExistingFiles([]);
      }
    }
  }, [existingExpense]);



  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues(prevValues => ({
      ...prevValues,
      [name]: value
    }));
  };

  const getExpenseTypeLabel = useCallback((type) => {
    const types = {
      'hotel': 'Hôtel',
      'restaurant': 'Restaurant', 
      'carburant': 'Carburant',
      'taxi': 'Taxi',
      'carRental': 'Location de véhicule',
      'plane': 'Avion',
      'distance_km': 'Frais kilométriques',
      'other': 'Autre'
    };
    return types[type] || 'Autre';
  }, []);

  // Effet pour gérer les données OCR venant du menu
  useEffect(() => {
    if (location.state?.ocrData) {
      const ocrData = location.state.ocrData;
      
      // Vérifier si les données OCR ont déjà été traitées pour éviter les redoublements
      const ocrProcessedKey = `ocr_processed_${noteId}_${ocrData.justificatif || Date.now()}`;
      const alreadyProcessed = sessionStorage.getItem(ocrProcessedKey);
      
      setFormValues(prevValues => buildValuesFromOCR(prevValues, ocrData));
      
      // === AJOUTER L'IMAGE OCR COMME JUSTIFICATIF AUTOMATIQUE (menu) ===
      if (ocrData.justificatif && ocrData.autoSavedAsJustificatif && !alreadyProcessed) {
        const ocrImageFile = {
          name: ocrData.justificatif,
          path: ocrData.justificatif,
          type: 'image/jpeg',
          isOCRAutoAdded: true
        };
        
        // Ajouter l'image OCR aux fichiers existants
        setExistingFiles(prevFiles => {
          const alreadyExists = prevFiles.some(file => file.name === ocrData.justificatif);
          if (!alreadyExists) {
            return [...prevFiles, ocrImageFile];
          }
          return prevFiles;
        });
        
        // Marquer comme traité avant d'afficher le message
        sessionStorage.setItem(ocrProcessedKey, 'true');
        
        // Message de confirmation pour le menu OCR
        setTimeout(() => {
          Swal.fire({
            icon: 'success',
            title: 'Justificatif temporaire',
            text: 'L\'image OCR a été ajoutée à la dépense et sera sauvegardée à la validation de la note',
            confirmButtonText: 'OK',
            timer: 2500,
            timerProgressBar: true
          });
        }, 500);
      }

      // Si le type de dépense détecté est différent, l'afficher aussi (seulement si pas déjà traité)
      if (ocrData.expense_type && ocrData.expense_type !== expenseType && !alreadyProcessed) {
        setTimeout(() => {
          Swal.fire({
            icon: 'info',
            title: 'Type de dépense détecté',
            html: `
              <p>L'OCR a détecté un type de dépense différent :</p>
              <p><strong>Détecté :</strong> ${getExpenseTypeLabel(ocrData.expense_type)}</p>
              <p><strong>Formulaire actuel :</strong> ${getExpenseTypeLabel(expenseType)}</p>
              <p>Les données ont été appliquées au formulaire actuel.</p>
            `,
            confirmButtonText: 'OK'
          });
        }, 2500);
      }
    }
  }, [location.state?.ocrData, buildValuesFromOCR, expenseType, getExpenseTypeLabel, noteId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateExpenseForm(formValues, expenseType)) {
      return;
    }

    const mainDate = getMainDate(formValues, expenseType);
    const formData = new FormData();
    
    // Ajout des fichiers
    fileInputs.forEach(file => {
      formData.append('justificatifs', file);
    });

    // Ajout des données du formulaire
    formData.append('noteId', noteId);
    formData.append('expenseType', expenseType);
    formData.append('amount', formValues.amount);
    formData.append('description', formValues.description);
    formData.append('date', mainDate);
    formData.append('tva', formValues.tva);
    

    
    // Ajout des données spécifiques
    const specificData = {};
    preset.fields.forEach(field => {
      if (field.id !== 'amount' && field.id !== 'description' && field.id !== 'date') {
        specificData[field.id] = formValues[field.id];
      }
    });

    specificData.montantAvecTva = parseAmountValue(formValues.amount);
    specificData.montantHtCalcule = calculateHtFromTtc(formValues.amount, formValues.tva);
    
    // Ajouter les données OCR TVA si disponibles (sauvegarde manuelle)
    if (formValues.montantHT && formValues.montantTTC) {
      specificData.montantHT_OCR = formValues.montantHT;
      specificData.montantTTC_OCR = formValues.montantTTC;
      specificData.tvaIncluded_OCR = formValues.tvaIncluded;
    }
    
    formData.append('donnees_specifiques', JSON.stringify(specificData));

    // Ajout des justificatifs existants
    if (existingFiles.length > 0) {
      formData.append('existingFiles', JSON.stringify(existingFiles));
    }

    // Appeler directement onSave avec le formData
    // onSave se chargera des SweetAlert
    onSave(formData);
  };

  const renderFields = () => {
    // Rendre d'abord les champs de base
    const baseFields = [
      {
        id: 'amount',
        label: 'Montant TTC',
        type: 'number',
        required: true,
        placeholder: 'Montant TTC en euros'
      },
      {
        id: 'description',
        label: 'Description',
        type: 'text',
        required: true,
        placeholder: 'Description de la dépense'
      },
      {
        id: 'date',
        label: 'Date',
        type: 'date',
        required: true
      }
    ];

    // Rendre les champs de base
    const baseFieldsElements = baseFields.map(field => {
      const commonProps = {
        key: field.id,
        name: field.id,
        value: formValues[field.id] || '',
        onChange: handleChange,
        required: field.required,
        className: 'form-control'
      };

      switch (field.type) {
        case 'text':
          return (
            <div key={field.id} className="form-group">
              <label htmlFor={field.id}>{field.label}</label>
              <input
                type="text"
                {...commonProps}
                placeholder={field.placeholder}
              />
              {field.id === 'description' && formValues.descriptionNote && (
                <div className="description-note" style={{
                  marginTop: '5px',
                  padding: '8px',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeaa7',
                  borderRadius: '4px',
                  fontSize: '0.875em',
                  color: '#856404'
                }}>
                  {formValues.descriptionNote}
                </div>
              )}
            </div>
          );
        case 'number':
          // Pour les frais kilométriques, rendre le champ montant en lecture seule
          if (field.id === 'amount' && expenseType === 'distance_km') {
            return (
              <div key={field.id} className="form-group">
                <label htmlFor={field.id}>{field.label}</label>
                <input
                  type="number"
                  {...commonProps}
                  step="0.01"
                  min="0"
                  readOnly={true}
                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                />
                <small className="form-hint" style={{color: '#666', fontStyle: 'italic'}}>
                  Montant calculé automatiquement selon les barèmes gouvernementaux
                  {calculating && " (calcul en cours...)"}
                </small>
              </div>
            );
          }
          return (
            <div key={field.id} className="form-group">
              <label htmlFor={field.id}>{field.label}</label>
              <input
                type="number"
                {...commonProps}
                step="0.01"
                min="0"
              />
            </div>
          );
        case 'date':
          return (
            <div key={field.id} className="form-group">
              <label htmlFor={field.id}>{field.label}</label>
              <input
                type="date"
                {...commonProps}
                value={toInputDateFormat(formValues[field.id])}
              />
            </div>
          );
        default:
          return null;
      }
    });

    // Rendre ensuite les champs spécifiques
    const specificFields = preset.fields.map(field => {
      if (field.type === 'calculated') {
        return null;
      }

      const commonProps = {
        key: field.id,
        name: field.id,
        value: formValues[field.id] || '',
        onChange: handleChange,
        required: field.required,
        className: 'form-control'
      };

      switch (field.type) {
        case 'text':
          return (
            <div key={field.id} className="form-group">
              <label htmlFor={field.id}>{field.label}</label>
              <input
                type="text"
                {...commonProps}
                placeholder={field.placeholder}
              />
            </div>
          );
        case 'number':
          return (
            <div key={field.id} className="form-group">
              <label htmlFor={field.id}>{field.label}</label>
              <input
                type="number"
                {...commonProps}
                step={field.step || "0.01"}
                min="0"
                readOnly={field.readonly}
                style={field.readonly ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
              />
              {field.readonly && (
                <small className="form-hint" style={{color: '#666', fontStyle: 'italic'}}>
                  Taux fixé automatiquement selon votre profil
                </small>
              )}
            </div>
          );
        case 'address':
          return (
            <div key={field.id} className="form-group kilometric-address">
              <label htmlFor={field.id}>
                {field.icon && <i className={`${field.icon} address-field-icon`}></i>}
                {field.label}
              </label>
              <div className="address-field-with-icon">
                <AddressAutocomplete
                  name={field.id}
                  value={formValues[field.id] || ''}
                  onChange={handleChange}
                  placeholder={field.placeholder}
                  required={field.required}
                  onAddressSelect={(addressData) => handleAddressSelect(field.id, addressData)}
                />
              </div>
              {field.id === 'destination_dkm' && distanceInfo.isCalculating && (
                <small className="form-hint" style={{color: '#007bff'}}>
                  <i className="fas fa-spinner fa-spin"></i> Calcul de la distance en cours...
                </small>
              )}
              {field.id === 'destination_dkm' && distanceInfo.error && (
                <small className="form-hint" style={{color: '#dc3545'}}>
                  <i className="fas fa-exclamation-triangle"></i> {distanceInfo.error}
                </small>
              )}
              {field.id === 'destination_dkm' && distanceInfo.duration && !distanceInfo.isCalculating && (
                <small className="form-hint" style={{color: '#28a745'}}>
                  <i className="fas fa-check-circle"></i> 
                  Distance calculée • Durée estimée: {formatDuration(distanceInfo.duration)}
                  {distanceInfo.method === 'crow_flies' && ' (estimation)'}
                </small>
              )}
            </div>
          );
        case 'date':
          return (
            <div key={field.id} className="form-group">
              <label htmlFor={field.id}>{field.label}</label>
              <input
                type="date"
                {...commonProps}
                value={toInputDateFormat(formValues[field.id])}
              />
            </div>
          );
        default:
          return null;
      }
    });

    // Champ TVA intelligent (masqué pour certains types de dépenses)
    const tvaField = preset.noTva ? null : (
      <div key="tva" className="form-group tva-section">
        <label htmlFor="tva">TVA (%)</label>
        <div className="tva-controls">
          <select
            name="tva"
            value={formValues.tvaCustom ? 'custom' : (formValues.tva !== undefined && formValues.tva !== null && formValues.tva !== '' ? formValues.tva : 20)}
            onChange={(e) => {
              if (e.target.value === 'custom') {
                setFormValues(prev => ({ ...prev, tvaCustom: true, tva: '' }));
              } else {
                setFormValues(prev => ({ ...prev, tvaCustom: false, tva: e.target.value }));
              }
            }}
            className="form-control tva-select"
          >
            <option value={0}>0% - Exonérée</option>
            <option value={2.1}>2,1% - Médicaments</option>
            <option value={5.5}>5,5% - Alimentation</option>
            <option value={10}>10% - Restaurant/Transport/Hôtel</option>
            <option value={20}>20% - Taux normal</option>
            <option value="custom">Saisie manuelle</option>
          </select>
          
          {/* Champ de saisie manuelle de la TVA */}
          {formValues.tvaCustom && (
            <input
              type="number"
              name="tva"
              value={formValues.tva}
              onChange={handleChange}
              placeholder="Saisissez le taux de TVA"
              className="form-control"
              style={{ marginTop: '8px' }}
              step="0.01"
              min="0"
              max="100"
            />
          )}
          
          {/* Affichage des montants HT/TTC si disponibles via OCR */}
          {formValues.montantHT && formValues.montantTTC && (
            <div className="tva-breakdown">
              <small className="tva-info">
                <strong>Détecté par OCR :</strong><br/>
                Montant HT: {parseAmountValue(formValues.montantHT).toFixed(2)}€<br/>
                Montant TTC: {parseAmountValue(formValues.amount).toFixed(2)}€<br/>
                TVA {formValues.tva}% = {(formValues.montantTTC - formValues.montantHT).toFixed(2)}€
              </small>
            </div>
          )}
          
          {/* Calculateur automatique si pas de données OCR */}
          {!formValues.montantHT && formValues.amount && formValues.tva > 0 && (
            <div className="tva-calculator">
              <small className="tva-calculation">
                <strong>Calcul automatique :</strong><br/>
                TTC saisi: {parseAmountValue(formValues.amount).toFixed(2)}€<br/>
                HT recalculé: {calculateHtFromTtc(formValues.amount, formValues.tva).toFixed(2)}€ | TVA {formValues.tva}%: {(parseAmountValue(formValues.amount) - calculateHtFromTtc(formValues.amount, formValues.tva)).toFixed(2)}€
              </small>
            </div>
          )}
        </div>
      </div>
    );

    // Affichage des informations kilométriques pour les frais kilométriques
    const kilometricInfoField = expenseType === 'distance_km' && (kilometric_info || calculating) ? (
      <div key="kilometric-info" className="form-group kilometric-info-section">
        <label>Informations kilométriques</label>
        <div className="kilometric-info-content" style={{
          padding: '12px',
          backgroundColor: kilometric_info?.error ? '#fff5f5' : '#f8f9fa',
          border: `1px solid ${kilometric_info?.error ? '#feb2b2' : '#e9ecef'}`,
          borderRadius: '6px',
          fontSize: '0.9rem'
        }}>
          {calculating ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ 
                width: '16px', 
                height: '16px', 
                border: '2px solid #45a049',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <span>Calcul en cours selon les tranches gouvernementales...</span>
            </div>
          ) : kilometric_info ? (
            <div>
              {kilometric_info.error ? (
                <div style={{ 
                  padding: '8px', 
                  backgroundColor: '#fed7d7',
                  border: '1px solid #feb2b2',
                  borderRadius: '4px',
                  marginBottom: '12px',
                  color: '#c53030'
                }}>
                  <strong>⚠️ Problème de calcul:</strong> {kilometric_info.error}
                  <br/>
                  <small style={{ fontSize: '0.8rem' }}>
                    Calcul de base appliqué. Veuillez vous reconnecter pour obtenir le calcul complet selon les tranches gouvernementales.
                  </small>
                </div>
              ) : null}
              
              <div style={{ marginBottom: '8px' }}>
                <strong>Véhicule:</strong> {kilometric_info.cv > 0 ? `${kilometric_info.cv} CV` : 'Non disponible'}
              </div>
              
              <div style={{ marginBottom: '8px' }}>
                <strong>Distance saisie:</strong> {formValues.distance_parcourue} km
              </div>
              
              {!kilometric_info.error && (
                <>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Distance annuelle actuelle:</strong> {kilometric_info.distance_annuelle_actuelle?.toFixed(0)} km
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Nouvelle distance annuelle:</strong> {kilometric_info.nouvelle_distance_annuelle?.toFixed(0)} km
                  </div>
                </>
              )}
              
              <div style={{ 
                padding: '8px', 
                backgroundColor: kilometric_info.error ? '#ffeaa7' : (
                  kilometric_info.detail?.tranche_actuelle === 1 ? '#d4edda' : 
                  kilometric_info.detail?.tranche_actuelle === 2 ? '#fff3cd' : '#f8d7da'
                ),
                borderRadius: '4px',
                border: `1px solid ${kilometric_info.error ? '#ffc107' : (
                  kilometric_info.detail?.tranche_actuelle === 1 ? '#c3e6cb' : 
                  kilometric_info.detail?.tranche_actuelle === 2 ? '#ffeaa7' : '#f5c6cb'
                )}`
              }}>
                <strong>Tranche tarifaire:</strong> 
                {kilometric_info.error ? " Calcul de base (taux fixe)" : (
                  <>
                    {kilometric_info.detail?.tranche_actuelle === 1 && " Tranche 1 (≤ 5 000 km)"}
                    {kilometric_info.detail?.tranche_actuelle === 2 && " Tranche 2 (5 001 - 20 000 km)"}
                    {kilometric_info.detail?.tranche_actuelle === 3 && " Tranche 3 (> 20 000 km)"}
                  </>
                )}
              </div>
              
              <div style={{ marginTop: '8px', fontSize: '0.8rem', color: '#666' }}>
                <em>
                  {kilometric_info.error 
                    ? 'Calcul de base appliqué en raison du problème de connexion'
                    : 'Calcul conforme aux barèmes gouvernementaux en vigueur'
                  }
                </em>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    ) : null;

    return [...baseFieldsElements, ...specificFields, kilometricInfoField, tvaField].filter(Boolean);
  };

  return (
    <>
      <form id={formId} onSubmit={handleSubmit} className="expense-form">
        <div className="form-fields-container">
          
          <div className="form-fields">
            {renderFields()}
          </div>

          <div className="file-upload-section">
            <label htmlFor="file-upload" className="file-upload-label">
              Ajouter des justificatifs
            </label>
            <input
              id="file-upload"
              type="file"
              multiple
              onChange={(e) => handleFileChange(e, setFileInputs)}
              className="file-upload-input"
            />
            
            {/* Afficher tous les justificatifs (existants + nouveaux) */}
            {(existingFiles.length > 0 || fileInputs.length > 0) && (
              <div className="all-files">
                <h4>Tous les justificatifs :</h4>
                
                {/* Fichiers existants */}
              {existingFiles.map((file, idx) => (
                  <div key={`existing-${idx}`} className="added-file">
                    <span className="file-name">{file.name} <em>(existant)</em></span>
                  <div className="file-actions">
                    <button
                      type="button"
                      onClick={() => handlePreviewExisting(file.path)}
                      className="preview-button"
                    >
                      Voir
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveExistingFile(idx, setExistingFiles)}
                      className="remove-button"
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              ))}
                
                {/* Nouveaux fichiers ajoutés */}
                {fileInputs.map((file, idx) => (
                  <div key={`new-${idx}`} className="added-file">
                    <span className="file-name">{file.name} <em>(nouveau)</em></span>
                    <div className="file-actions">
                      <button
                        type="button"
                        onClick={() => handlePreview([file])}
                        className="preview-button"
                      >
                        Voir
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const newFiles = fileInputs.filter((_, index) => index !== idx);
                          setFileInputs(newFiles);
                        }}
                        className="remove-button"
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                ))}
            </div>
            )}
          </div>

          {showSubmitButton && (
            <div className="form-actions">
              <button
                type="submit"
                className="btn-primary footer-center-button footer-submit-button expense-form-submit-button"
                aria-label={existingExpense ? 'Modifier la dépense' : 'Ajouter la dépense'}
                title={existingExpense ? 'Modifier la dépense' : 'Ajouter la dépense'}
              >
                <i className={`fa-solid ${existingExpense ? 'fa-check' : 'fa-plus'}`}></i>
              </button>
            </div>
          )}
        </div>
      </form>
    </>
  );
};

export default ExpenseForm; 
